export const parseOrdersForTimeline = (orders) => {
  const parsedOrders = orders.map((order) => ({
    start: new Date(order.dateFrom),
    end: new Date(order.dateTo), // end is optional
    content: order.firstName,
    group: order.propertyName,
    // group: order.propertyId, // TODO: Consider changing to propertyId if needed
    className: 'itemClassName',
    editable: true,
    createdAt: order.createdAt,
    costPerNight: order.costPerNight,
    email: order.email,
    firstName: order.firstName,
    id: order.id,
    phone: order.phone,
    propertyId: order.propertyId,
    status: order.status,
    totalCost: order.totalCost,
    updatedAt: order.updatedAt,
    description: order.description,
    surname: order.surname,
    feeding: order.feeding,
  }))

  return (
    parsedOrders
  );
};

export const parseGroupForTimeline = (propertyData) => (
  propertyData.map((property) => ({
    id: property.title, // need to be title to work with edit item group autoselect
    content: property.title,
    title: property.title,
    costPerNight: property.costPerNight,
    createdAt: property.createdAt,
    propertyId: property.id,
    updatedAt: property.updatedAt,
    userId: property.userId,
    className: !property.isAvailable ? 'groupDisabled' : ''
  }))
);

const currentYear = new Date().getFullYear();
const startYear = 2023;

export const yearsSelectOptions = Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
  const year = startYear + index;

  return { name: year.toString(), value: year.toString() };
});

export const getObjectById = (arrayOfObjects, id) => {
  if (arrayOfObjects?.length) {
    return arrayOfObjects.find(obj => obj.id === id);
  }

  return;
};

// --- Timeline helpers and constants ---
export const DAY_MS = 24 * 60 * 60 * 1000;
export const ITEM_OFFSET_PX = 20;
export const START_OFFSET_PCT = 0.5; // percent visual offset for item start
export const END_OFFSET_PCT = 1.4; // percent visual offset for item end
export const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const getDayKey = (date) => DAY_KEYS[date.getDay()];
export const isSunday = (date) => date.getDay() === 0;

export const normalizeToLocalNoon = (input) => {
  const d = new Date(input);
  d.setHours(12, 0, 0, 0);
  return d;
};

// Returns { startDate, days } for visible range. Uses selectedDate if provided,
// otherwise derives range from items and snaps to full months.
export const getVisibleMonthRange = (items = [], selectedDate) => {
  if (selectedDate && selectedDate.dateFrom) {
    const from = new Date(selectedDate.dateFrom);
    const start = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(from.getFullYear(), from.getMonth() + 1, 0);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    let monthDays = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      monthDays++;
    }

    return { startDate: start, days: monthDays };
  }

  if (!items?.length) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    let monthDays = 0;
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      monthDays++;
    }
    return { startDate: start, days: monthDays };
  }

  const minItemDate = new Date(Math.min(...items.map(i => new Date(i.start).getTime())));
  const maxItemDate = new Date(Math.max(...items.map(i => new Date(i.end).getTime())));

  const start = new Date(minItemDate.getFullYear(), minItemDate.getMonth(), 1);
  const end = new Date(maxItemDate.getFullYear(), maxItemDate.getMonth() + 1, 0);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  let daysCount = 0;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    daysCount++;
  }

  return { startDate: start, days: daysCount };
};

export const buildDayArray = (startDate, days) => {
  const arr = [];
  const d = new Date(startDate);
  for (let i = 0; i < days; i++) {
    arr.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return arr;
};

// Fractional day offset from startDate using local-noon anchoring to avoid DST
// and half-day overlap issues.
export const dayFractionFromStart = (date, startDate) => {
  const n = normalizeToLocalNoon(date).getTime();
  const s = normalizeToLocalNoon(startDate).getTime();
  return (n - s) / DAY_MS;
};

// Compute responsive offset values based on available width (px).
// Returns an object { startOffsetPct, endOffsetPct } in percent units.
export const computeOffsetsForWidth = (widthPx = 1024) => {
  // Breakpoints tuned empirically. Smaller screens need larger visual offsets
  // because a single percent of the timeline width becomes fewer pixels.
  if (widthPx < 480) {
    return { startOffsetPct: 4.0, endOffsetPct: 3.0 };
  }

  if (widthPx < 768) {
    return { startOffsetPct: 2.0, endOffsetPct: 2.5 };
  }

  if (widthPx < 1024) {
    return { startOffsetPct: 1.4, endOffsetPct: 2.4 };
  }

  if (widthPx < 1350) {
    return { startOffsetPct: 0.8, endOffsetPct: 2.1 };
  }

  if (widthPx < 1600) {
    return { startOffsetPct: 0.4, endOffsetPct: 1.7 };
  }

  // Desktop and larger: smaller percent offsets
  return { startOffsetPct: START_OFFSET_PCT, endOffsetPct: END_OFFSET_PCT };
};

